import {
    buildCollectionHistoryUrl,
    createPlpInserter,
    getBrowseResults,
    getSearchResults,
    hasActiveBrowseFilters,
    parseCollectionBrowseState,
    parseSortParam,
    readPlpModules,
} from './constructor-browse.js';
import { browseFiltersFromFormData, readBrowseForm } from './constructor-filters.js';
import { getCardTemplates, renderProductCard } from './constructor-product-card.js';

if (!customElements.get('collection-grid')) {
    const PRELOAD_LINK_DATA_ATTR = 'data-collection-grid-preload';

    const isUnmodifiedLeftClick = (event) =>
        event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

    const setControlDisabled = (el, isDisabled) => {
        if (!el) {
            return;
        }

        if (el instanceof HTMLButtonElement) {
            el.disabled = isDisabled;
        }
    };

    // Same window as snippets/collection_pagination.liquid: early pages, a
    // short run around the current page, and the last page, with an ellipsis
    // wherever more than one page is skipped.
    const visiblePaginationPages = (current, total) => {
        if (total <= 7) {
            return Array.from({ length: total }, (_, index) => index + 1);
        }

        const endZone = total - 3;
        const endStart = total - 2;
        const pages = [];

        for (let page = 1; page <= total; page += 1) {
            let show = false;

            if (page === 1 || page === total) {
                show = true;
            } else if (current <= 3 && page <= 3) {
                show = true;
            } else if (current === 3 && page === 4) {
                show = true;
            } else if (current > endZone) {
                if (page >= endStart) {
                    show = true;
                }

                if (current === endStart && page === endZone) {
                    show = true;
                }
            } else if (current > 3) {
                show = page >= current - 1 && page <= current + 1;
            }

            if (show) {
                pages.push(page);
            }
        }

        return pages;
    };

    const preloadImage = (src, srcset, sizes) => {
        if (!src) {
            return;
        }

        const selector = `link[rel="preload"][as="image"][href="${CSS.escape(src)}"], link[${PRELOAD_LINK_DATA_ATTR}="${CSS.escape(src)}"]`;

        if (document.head.querySelector(selector)) {
            return;
        }

        const elLink = document.createElement('link');
        elLink.rel = 'preload';
        elLink.as = 'image';
        elLink.href = src;

        if (srcset) {
            elLink.setAttribute('imagesrcset', srcset);
        }

        if (sizes) {
            elLink.setAttribute('imagesizes', sizes);
        }

        elLink.setAttribute(PRELOAD_LINK_DATA_ATTR, src);
        document.head.appendChild(elLink);
    };

    class CollectionGrid extends HTMLElement {
        #observer = null;

        #prefetchObserver = null;

        #abortController = null;

        #prefetchAbortController = null;

        #loading = false;

        #prefetching = false;

        #pendingFragment = null;

        #pendingPage = null;

        #pendingPageTotal = null;

        #cnstrcNumResults = null;

        #onPopState = null;

        #onPaginationClick = null;

        #browseState = null;

        #plpInserter = null;

        #pendingBrowse = null;

        #cardTemplates = null;

        #plpModules = [];

        #browseGeneration = 0;

        connectedCallback() {
            this.sectionId = this.getAttribute('section-id');
            this.elProducts = this.querySelector('[data-products]');
            this.elSentinel = this.querySelector('[data-sentinel]');
            this.elLoadMore = this.querySelector('[data-load-more]');
            this.elLoader = this.querySelector('[data-loader]');
            this.elStatus = this.querySelector('[data-status]');
            this.elCount = this.querySelector('[data-count]');
            this.elPagination = this.querySelector('[data-pagination]');
            this.elPagePrev = this.querySelector('[data-page-prev]');
            this.elPageNext = this.querySelector('[data-page-next]');
            this.elPageButtons = this.querySelectorAll('[data-page]');

            this.currentPage = Number(this.getAttribute('page-current'));
            this.pageTotal = Number(this.getAttribute('page-total'));
            this.infiniteScroll = this.getAttribute('infinite-scroll') !== 'false';

            this.labelLoading = this.dataset.labelLoading || 'Loading more products';
            this.labelLoaded = this.dataset.labelLoaded || '%count% more products loaded';
            this.labelError = this.dataset.labelError || 'Unable to load more products. Try again.';
            this.labelEnd = this.dataset.labelEnd || 'All products loaded';
            this.labelLoadingPage = this.dataset.labelLoadingPage || 'Loading products';
            this.labelHeadingResults = this.dataset.labelHeadingResults || '%count% results for “%terms%”';
            this.labelHeadingNoResults = this.dataset.labelHeadingNoResults || 'No results for “%terms%”';

            if (!this.sectionId || !this.elProducts) {
                return;
            }

            this.#updateFooterVisibility();

            this.#onPopState = () => {
                this.#syncFromUrl();
            };

            window.addEventListener('popstate', this.#onPopState);

            if (this.#isConstructorResults()) {
                this.#initBrowse();
                return;
            }

            if (this.pageTotal <= 1) {
                return;
            }

            if (this.infiniteScroll) {
                this.#initInfiniteScroll();
                return;
            }

            this.#bindPaginationEvents();
        }

        disconnectedCallback() {
            this.#observer?.disconnect();
            this.#prefetchObserver?.disconnect();
            this.#abortController?.abort();
            this.#prefetchAbortController?.abort();

            if (this.#onPopState) {
                window.removeEventListener('popstate', this.#onPopState);
            }

            if (this.elPagination && this.#onPaginationClick) {
                this.elPagination.removeEventListener('click', this.#onPaginationClick);
                this.#onPaginationClick = null;
            }
        }

        applyFilters(form) {
            if (this.#isConstructorResults()) {
                const params = readBrowseForm(form);
                const sort = parseSortParam(params.get('sort_by'));

                return this.#applyBrowse({
                    page: 1,
                    filters: browseFiltersFromFormData(form),
                    sortBy: sort.sortBy,
                    sortOrder: sort.sortOrder,
                    source: 'filter',
                    updateHistory: true,
                    fade: true,
                    reset: true,
                });
            }

            const url = new URL(form.action, window.location.origin);
            url.search = '';

            for (const [key, value] of new FormData(form).entries()) {
                if (value) {
                    url.searchParams.append(key, value);
                }
            }

            url.searchParams.delete('page');

            return this.applyFromUrl(this.#withSearchQuery(url).toString(), { source: 'filter' });
        }

        applyFromUrl(url, { updateHistory = true, source = 'filter' } = {}) {
            if (this.#isConstructorResults()) {
                const state = parseCollectionBrowseState(url);

                return this.#applyBrowse({
                    ...state,
                    source,
                    updateHistory,
                    fade: true,
                    reset: true,
                });
            }

            if (this.#loading) {
                return Promise.resolve();
            }

            const resolvedUrl = this.#withSearchQuery(url);
            const fetchUrl = this.#buildSectionUrl(resolvedUrl.toString());

            return this.#applySectionUpdate(fetchUrl, {
                updateHistory,
                historyUrl: this.#buildHistoryUrlFromUrl(resolvedUrl.toString()),
                source,
                fade: true,
                resetInfiniteScroll: true,
            });
        }

        #withSearchQuery(url) {
            const resolvedUrl = new URL(url, window.location.origin);
            const searchQuery =
                this.dataset.searchQuery || new URL(window.location.href).searchParams.get('q');

            if (searchQuery && !resolvedUrl.searchParams.get('q')) {
                resolvedUrl.searchParams.set('q', searchQuery);
            }

            return resolvedUrl;
        }

        #initInfiniteScroll() {
            this.elLoadMore?.addEventListener('click', (event) => {
                if (!isUnmodifiedLeftClick(event)) {
                    return;
                }

                event.preventDefault();
                this.#loadNextPage();
            });

            if (!this.elSentinel) {
                return;
            }

            this.#observer = new IntersectionObserver(
                (entries) => {
                    for (const entry of entries) {
                        if (entry.isIntersecting) {
                            this.#loadNextPage();
                        }
                    }
                },
                { rootMargin: '400px 0px' }
            );
            this.#observer.observe(this.elSentinel);

            this.#prefetchObserver = new IntersectionObserver(
                (entries) => {
                    for (const entry of entries) {
                        if (entry.isIntersecting) {
                            this.#prefetchNextPage();
                        }
                    }
                },
                { rootMargin: '1000px 0px' }
            );
            this.#prefetchObserver.observe(this.elSentinel);
        }

        #bindPaginationEvents() {
            if (!this.elPagination || this.#onPaginationClick) {
                return;
            }

            this.#onPaginationClick = (event) => {
                if (!isUnmodifiedLeftClick(event)) {
                    return;
                }

                const elTarget = event.target.closest('[data-page-prev], [data-page-next], [data-page]');

                if (!elTarget || !this.elPagination.contains(elTarget)) {
                    return;
                }

                event.preventDefault();

                if (elTarget.matches('[data-page-prev]')) {
                    this.#goToPage(this.currentPage - 1);
                    return;
                }

                if (elTarget.matches('[data-page-next]')) {
                    this.#goToPage(this.currentPage + 1);
                    return;
                }

                this.#goToPage(Number(elTarget.dataset.page));
            };

            this.elPagination.addEventListener('click', this.#onPaginationClick);
        }

        #syncFromUrl() {
            if (this.#isConstructorResults()) {
                const state = parseCollectionBrowseState(window.location.href);

                return this.#applyBrowse({
                    ...state,
                    source: 'popstate',
                    updateHistory: false,
                    fade: true,
                    reset: true,
                });
            }

            if (this.#loading) {
                return Promise.resolve();
            }

            const fetchUrl = this.#buildSectionUrl(window.location.href);

            return this.#applySectionUpdate(fetchUrl, {
                updateHistory: false,
                source: 'popstate',
                fade: true,
                resetInfiniteScroll: true,
            });
        }

        #applySectionUpdate(fetchUrl, {
            updateHistory = false,
            historyUrl = null,
            source = 'pagination',
            fade = true,
            resetInfiniteScroll = false,
        } = {}) {
            this.#clearConstructorResultCount();
            this.#setLoading(true);
            this.#announce(this.labelLoadingPage);

            if (fade) {
                this.elProducts.style.opacity = '0';
                window.scrollTo({
                    top: 0,
                    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                        ? 'auto'
                        : 'smooth',
                });
            }

            return this.#fetchSection(fetchUrl)
                .then((parsed) => {
                    const count = this.#renderSection(parsed, { resetInfiniteScroll });
                    this.elProducts.style.opacity = '1';

                    if (updateHistory && historyUrl) {
                        history.pushState({ collectionGridState: true }, '', historyUrl);
                    }

                    this.#setLoading(false);
                    this.#announce(this.labelLoaded.replace('%count%', String(count)));

                    this.dispatchEvent(
                        new CustomEvent('collection-grid:updated', {
                            bubbles: true,
                            detail: { elSection: parsed.elSection, source },
                        })
                    );
                })
                .catch((error) => {
                    if (error.name === 'AbortError') {
                        return;
                    }

                    this.elProducts.style.opacity = '1';
                    this.#restoreConstructorResultCount();
                    this.#setLoading(false);
                    this.#updatePaginationUI();
                    this.#announce(this.labelError);
                    console.error('collection-grid fetch failed', error);
                });
        }

        #renderSection(parsed, { resetInfiniteScroll = false } = {}) {
            const { elProducts, elPagination, elCount, pageCurrent, pageTotal, elGrid } = parsed;

            if (elGrid?.dataset.searchQuery) {
                this.dataset.searchQuery = elGrid.dataset.searchQuery;
            }

            this.elProducts.replaceChildren(...[...elProducts.children]);
            this.#replacePagination(elPagination);
            this.#replaceCount(elCount);

            this.currentPage = pageCurrent;
            this.setAttribute('page-current', String(pageCurrent));
            this.#syncConstructorResults(elGrid, pageCurrent);

            if (pageTotal) {
                this.pageTotal = pageTotal;
                this.setAttribute('page-total', String(pageTotal));
            }

            if (resetInfiniteScroll && this.infiniteScroll) {
                this.#resetInfiniteScrollState();
            }

            this.#updatePaginationUI();
            this.#updateFooterVisibility();
            this.#promoteViewportImages();
            window.initLazyLoad?.();

            return this.#countProductCards(this.elProducts);
        }

        #countProductCards(root) {
            return root.querySelectorAll('product-card').length;
        }

        #appendProductNodes(elSource) {
            for (const child of [...elSource.children]) {
                if (child.matches('[data-empty-state]')) {
                    continue;
                }

                const blockId = child.dataset.collectionGridBlock;

                if (blockId && this.elProducts.querySelector(`[data-collection-grid-block="${CSS.escape(blockId)}"]`)) {
                    continue;
                }

                this.elProducts.appendChild(child);
            }
        }

        #replacePagination(elPagination) {
            if (!this.elPagination) {
                return;
            }

            if (elPagination) {
                this.elPagination.innerHTML = elPagination.innerHTML;
                this.elPagination.hidden = false;
            } else {
                this.elPagination.hidden = true;
            }

            this.elPagePrev = this.querySelector('[data-page-prev]');
            this.elPageNext = this.querySelector('[data-page-next]');
            this.elPageButtons = this.querySelectorAll('[data-page]');
        }

        #replaceCount(elCount) {
            if (this.elCount && elCount) {
                this.elCount.textContent = elCount.textContent;
            }
        }

        #updateFooterVisibility() {
            const hasMultiplePages = this.pageTotal > 1;
            const hasProducts = Boolean(this.elProducts?.querySelector('product-card'));

            if (this.elPagination) {
                this.elPagination.hidden = !hasMultiplePages || !hasProducts || this.infiniteScroll;
            }

            const elFooter = this.querySelector('.collection-grid--footer');

            if (elFooter) {
                elFooter.hidden = !hasMultiplePages || !hasProducts;
            }
        }

        #resetInfiniteScrollState() {
            this.#clearPrefetch();
            this.#prefetchAbortController?.abort();

            if (this.elSentinel) {
                this.elSentinel.hidden = this.pageTotal <= 1;
            }

            if (this.elLoadMore) {
                this.elLoadMore.hidden = this.pageTotal <= 1;
                setControlDisabled(this.elLoadMore, false);
            }

            if (this.elLoader) {
                this.elLoader.hidden = true;
                this.elLoader.setAttribute('aria-hidden', 'true');
            }

            if (this.pageTotal <= 1) {
                this.#observer?.disconnect();
                this.#prefetchObserver?.disconnect();
                return;
            }

            if (this.currentPage >= this.pageTotal) {
                this.#setComplete();
                return;
            }

            if (this.elSentinel && !this.#observer) {
                this.#initInfiniteScroll();
            } else if (this.elSentinel && this.#observer) {
                this.#observer.observe(this.elSentinel);
                this.#prefetchObserver?.observe(this.elSentinel);
            }

            this.#prefetchNextPage();
        }

        #announce(message) {
            if (this.elStatus) {
                this.elStatus.textContent = message;
            }
        }

        #clearConstructorResultCount() {
            if (!this.hasAttribute('data-cnstrc-search') && !this.hasAttribute('data-cnstrc-browse')) return;
            if (!this.hasAttribute('data-cnstrc-num-results')) return;

            this.#cnstrcNumResults = this.getAttribute('data-cnstrc-num-results');
            this.removeAttribute('data-cnstrc-num-results');
        }

        #restoreConstructorResultCount() {
            if (this.#cnstrcNumResults == null) return;

            this.setAttribute('data-cnstrc-num-results', this.#cnstrcNumResults);
            this.#cnstrcNumResults = null;
        }

        #syncConstructorResults(elGrid, page) {
            if (this.hasAttribute('data-cnstrc-search') || this.hasAttribute('data-cnstrc-browse')) {
                const nextCount = elGrid?.getAttribute('data-cnstrc-num-results');

                if (nextCount != null) {
                    this.setAttribute('data-cnstrc-num-results', nextCount);
                    this.#cnstrcNumResults = null;
                }
            }

            const nextTerm = elGrid?.getAttribute('data-cnstrc-search-term');

            if (this.hasAttribute('data-cnstrc-search-term') && nextTerm != null) {
                this.setAttribute('data-cnstrc-search-term', nextTerm);
            }

            this.#syncConstructorResultPage(page);
        }

        #syncConstructorResultPage(page) {
            if (!this.hasAttribute('data-cnstrc-result-page') || page == null) return;

            this.setAttribute('data-cnstrc-result-page', String(page));
        }

        #setLoading(isLoading) {
            this.#loading = isLoading;
            this.classList.toggle('loading', isLoading);

            if (this.elLoader) {
                this.elLoader.hidden = !isLoading;
                this.elLoader.setAttribute('aria-hidden', String(!isLoading));
            }

            setControlDisabled(this.elLoadMore, isLoading);

            if (this.elPagePrev) {
                this.elPagePrev.hidden = this.currentPage <= 1;
                setControlDisabled(this.elPagePrev, isLoading);
            }

            if (this.elPageNext) {
                this.elPageNext.hidden = this.currentPage >= this.pageTotal;
                setControlDisabled(this.elPageNext, isLoading);
            }

            for (const elPageButton of this.elPageButtons) {
                setControlDisabled(elPageButton, isLoading);
            }
        }

        #setComplete() {
            this.#observer?.disconnect();
            this.#prefetchObserver?.disconnect();
            this.#clearPrefetch();

            if (this.elSentinel) {
                this.elSentinel.hidden = true;
            }

            if (this.elLoadMore) {
                this.elLoadMore.hidden = true;
            }

            if (this.elLoader) {
                this.elLoader.hidden = true;
                this.elLoader.setAttribute('aria-hidden', 'true');
            }

            this.#announce(this.labelEnd);
        }

        #clearPrefetch() {
            this.#pendingFragment = null;
            this.#pendingPage = null;
            this.#pendingPageTotal = null;
            this.#pendingBrowse = null;
        }

        #buildSectionUrl(url) {
            const sectionUrl = new URL(url, window.location.origin);
            sectionUrl.searchParams.set('section_id', this.sectionId);
            return sectionUrl.toString();
        }

        #buildPageUrl(page) {
            const url = new URL(window.location.href);
            url.searchParams.set('page', String(page));
            return this.#buildSectionUrl(url.toString());
        }

        #buildHistoryUrlFromPage(page) {
            const url = new URL(window.location.href);

            if (page === 1) {
                url.searchParams.delete('page');
            } else {
                url.searchParams.set('page', String(page));
            }

            url.searchParams.delete('section_id');
            return url.toString();
        }

        #buildHistoryUrlFromUrl(url) {
            const historyUrl = new URL(url, window.location.origin);
            const page = Number(historyUrl.searchParams.get('page') || 1);

            if (page === 1) {
                historyUrl.searchParams.delete('page');
            }

            historyUrl.searchParams.delete('section_id');
            return historyUrl.toString();
        }

        #parseSectionResponse(responseText) {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const elSection = html.querySelector(`#shopify-section-${this.sectionId}`);
            const elProducts = elSection?.querySelector('[data-products]');
            const elGrid = elSection?.querySelector('collection-grid');
            const elPagination = elSection?.querySelector('[data-pagination]');
            const elCount = elSection?.querySelector('[data-count]');
            const elFiltersForm = elSection?.querySelector('.collection-filters--form');

            if (!elProducts) {
                throw new Error('No products container in response');
            }

            return {
                elSection,
                elProducts,
                elPagination,
                elCount,
                elFiltersForm,
                elGrid,
                pageCurrent: elGrid ? Number(elGrid.getAttribute('page-current')) : 1,
                pageTotal: elGrid ? Number(elGrid.getAttribute('page-total')) : this.pageTotal,
            };
        }

        #preloadPrimaryImages(root) {
            for (const elImg of root.querySelectorAll('[data-image] > img:not(.hover)')) {
                preloadImage(elImg.src, elImg.srcset || '', elImg.sizes || '');
            }
        }

        #promoteViewportImages() {
            for (const elImg of this.elProducts.querySelectorAll('[data-image] > img:not(.hover)')) {
                const { top, bottom } = elImg.getBoundingClientRect();

                if (top < window.innerHeight && bottom > 0) {
                    elImg.loading = 'eager';
                }
            }
        }

        #updatePaginationUI() {
            if (!this.elPagination) {
                return;
            }

            if (this.elPagePrev) {
                this.elPagePrev.hidden = this.currentPage <= 1;
                setControlDisabled(this.elPagePrev, this.#loading);
            }

            if (this.elPageNext) {
                this.elPageNext.hidden = this.currentPage >= this.pageTotal;
                setControlDisabled(this.elPageNext, this.#loading);
            }

            for (const elPageButton of this.elPageButtons) {
                const isCurrentPage = Number(elPageButton.dataset.page) === this.currentPage;

                if (isCurrentPage) {
                    elPageButton.setAttribute('aria-current', 'page');
                } else {
                    elPageButton.removeAttribute('aria-current');
                }
            }
        }

        #fetchSection(url) {
            this.#abortController?.abort();
            this.#abortController = new AbortController();

            return fetch(url, { signal: this.#abortController.signal })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }

                    return response.text();
                })
                .then((responseText) => {
                    const parsed = this.#parseSectionResponse(responseText);

                    this.#preloadPrimaryImages(parsed.elProducts);

                    return parsed;
                });
        }

        #fetchPage(page) {
            return this.#fetchSection(this.#buildPageUrl(page)).then((parsed) => ({
                elProducts: parsed.elProducts,
                elPagination: parsed.elPagination,
                pageTotal: parsed.pageTotal,
            }));
        }

        #finalizePage(nextPage, pageTotal, count) {
            this.currentPage = nextPage;
            this.setAttribute('page-current', String(nextPage));
            this.#syncConstructorResultPage(nextPage);

            if (pageTotal) {
                this.pageTotal = pageTotal;
                this.setAttribute('page-total', String(pageTotal));
            }

            this.#promoteViewportImages();
            window.initLazyLoad?.();

            this.#setLoading(false);
            this.#announce(this.labelLoaded.replace('%count%', String(count)));

            if (this.currentPage >= this.pageTotal) {
                this.#setComplete();
                return;
            }

            this.#prefetchNextPage();
        }

        #goToPage(page, { updateHistory = true } = {}) {
            if (this.#isConstructorResults()) {
                if (this.#loading || page < 1 || page > this.pageTotal || page === this.currentPage) {
                    return Promise.resolve();
                }

                return this.#applyBrowse({
                    ...this.#browseState,
                    page,
                    source: 'pagination',
                    updateHistory,
                    fade: true,
                    reset: true,
                });
            }

            if (
                this.#loading ||
                page < 1 ||
                page > this.pageTotal ||
                page === this.currentPage
            ) {
                return Promise.resolve();
            }

            const historyUrl = this.#buildHistoryUrlFromPage(page);

            return this.#applySectionUpdate(this.#buildPageUrl(page), {
                updateHistory,
                historyUrl,
                source: 'pagination',
                fade: true,
                resetInfiniteScroll: false,
            });
        }

        #prefetchNextPage() {
            if (this.#isConstructorResults()) {
                this.#prefetchBrowseNextPage();
                return;
            }

            const nextPage = this.currentPage + 1;

            if (this.#prefetching || this.#loading || this.currentPage >= this.pageTotal) {
                return;
            }

            if (this.#pendingPage === nextPage) {
                return;
            }

            this.#prefetching = true;
            this.#prefetchAbortController?.abort();
            this.#prefetchAbortController = new AbortController();

            fetch(this.#buildPageUrl(nextPage), { signal: this.#prefetchAbortController.signal })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }

                    return response.text();
                })
                .then((responseText) => {
                    const parsed = this.#parseSectionResponse(responseText);
                    const { elProducts, pageTotal } = parsed;

                    this.#preloadPrimaryImages(elProducts);

                    this.#pendingPage = nextPage;
                    this.#pendingPageTotal = pageTotal;
                    this.#pendingFragment = document.createDocumentFragment();

                    for (const child of [...elProducts.children]) {
                        if (child.matches('[data-empty-state]')) {
                            continue;
                        }

                        const blockId = child.dataset.collectionGridBlock;

                        if (blockId && this.elProducts.querySelector(`[data-collection-grid-block="${CSS.escape(blockId)}"]`)) {
                            continue;
                        }

                        this.#pendingFragment.appendChild(child);
                    }
                })
                .catch((error) => {
                    if (error.name === 'AbortError') {
                        return;
                    }

                    console.error('collection-grid prefetch failed', error);
                })
                .finally(() => {
                    this.#prefetching = false;
                });
        }

        #loadNextPage() {
            if (this.#isConstructorResults()) {
                this.#loadBrowseNextPage();
                return;
            }

            if (this.#loading || this.currentPage >= this.pageTotal) {
                return;
            }

            const nextPage = this.currentPage + 1;

            this.#setLoading(true);
            this.#announce(this.labelLoading);

            if (this.#pendingFragment && this.#pendingPage === nextPage) {
                this.#prefetchAbortController?.abort();

                const count = this.#countProductCards(this.#pendingFragment);
                const pageTotal = this.#pendingPageTotal;
                this.elProducts.appendChild(this.#pendingFragment);
                this.#clearPrefetch();
                this.#finalizePage(nextPage, pageTotal, count);
                return;
            }

            this.#fetchPage(nextPage)
                .then(({ elProducts, pageTotal }) => {
                    const count = this.#countProductCards(elProducts);

                    this.#appendProductNodes(elProducts);

                    this.#clearPrefetch();
                    this.#finalizePage(nextPage, pageTotal, count);
                })
                .catch((error) => {
                    if (error.name === 'AbortError') {
                        return;
                    }

                    this.#setLoading(false);
                    this.#announce(this.labelError);
                    console.error('collection-grid fetch failed', error);
                });
        }

        #isBrowse() {
            return this.hasAttribute('data-cnstrc-browse') && !this.hasAttribute('data-cnstrc-search');
        }

        #isSearch() {
            return this.hasAttribute('data-cnstrc-search');
        }

        #isConstructorResults() {
            return this.#isBrowse() || this.#isSearch();
        }

        #searchQuery() {
            const fromUrl = new URL(window.location.href).searchParams.get('q');

            if (fromUrl != null && fromUrl !== '') {
                return fromUrl;
            }

            return this.dataset.searchQuery || this.getAttribute('data-cnstrc-search-term') || '';
        }

        #fetchConstructorResults({
            page = 1,
            resultsPerPage = 12,
            filters = {},
            sortBy,
            sortOrder,
            signal,
        } = {}) {
            if (this.#isSearch()) {
                return getSearchResults({
                    query: this.#searchQuery(),
                    page,
                    resultsPerPage,
                    filters,
                    sortBy,
                    sortOrder,
                    signal,
                });
            }

            return getBrowseResults({
                filterName: this.getAttribute('data-cnstrc-filter-name') || 'group_id',
                filterValue: this.getAttribute('data-cnstrc-filter-value'),
                page,
                resultsPerPage,
                filters,
                sortBy,
                sortOrder,
                signal,
            });
        }

        #updateSearchHeading(total) {
            if (!this.#isSearch()) {
                return;
            }

            const heading = this.closest('section')?.querySelector('[data-search-heading]');

            if (!heading) {
                return;
            }

            const terms = this.#searchQuery();
            const template = Number(total) > 0 ? this.labelHeadingResults : this.labelHeadingNoResults;

            heading.textContent = template.replaceAll('%count%', String(total)).replaceAll('%terms%', terms);
        }

        #dispatchConstructorUpdated(payload, { source, filters, sortBy, sortOrder } = {}) {
            this.dispatchEvent(
                new CustomEvent('collection-grid:updated', {
                    bubbles: true,
                    detail: {
                        source,
                        browse: this.#isBrowse(),
                        search: this.#isSearch(),
                        facets: payload.response?.facets || [],
                        sortOptions: payload.response?.sort_options || [],
                        filters,
                        sortBy,
                        sortOrder,
                        totalNumResults: payload.response?.total_num_results || 0,
                    },
                })
            );
        }

        #prefersReducedMotion() {
            return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        }

        #initBrowse() {
            this.#cardTemplates = getCardTemplates(this);

            if (!this.#cardTemplates?.card) {
                console.error('collection-grid missing product card template');
                this.classList.remove('loading');
                this.#announce(this.labelError);
                return;
            }

            this.#plpModules = readPlpModules(this.querySelector('[data-plp-modules]')?.content);
            this.#browseState = parseCollectionBrowseState(window.location.href);
            this.elFooter = this.querySelector('[data-grid-footer]');

            if (this.infiniteScroll) {
                this.#initInfiniteScroll();
            } else {
                this.#bindPaginationEvents();
            }

            this.#applyBrowse({
                ...this.#browseState,
                source: 'init',
                updateHistory: false,
                fade: false,
                reset: true,
            });
        }

        #applyBrowse({
            page = 1,
            filters = {},
            sortBy = 'relevance',
            sortOrder = 'descending',
            source = 'pagination',
            updateHistory = false,
            fade = true,
            reset = false,
        } = {}) {
            const generation = ++this.#browseGeneration;
            this.#abortController?.abort();
            this.#prefetchAbortController?.abort();
            this.#abortController = new AbortController();
            this.#clearPrefetch();

            this.#browseState = { page, filters, sortBy, sortOrder };
            this.#clearConstructorResultCount();
            this.#setLoading(true);
            this.#announce(this.labelLoadingPage);

            if (fade) {
                if (!this.#prefersReducedMotion()) {
                    this.elProducts.style.opacity = '0';
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                    window.scrollTo(0, 0);
                }
            }

            const pageSize = Number(this.getAttribute('page-size')) || 12;

            if (this.#isSearch() && !this.#searchQuery().trim()) {
                const payload = {
                    response: {
                        results: [],
                        facets: [],
                        sort_options: [],
                        total_num_results: 0,
                    },
                };

                this.#renderBrowsePayload(payload, { append: false, reset: true, page: 1 });
                this.elProducts.style.opacity = '1';
                this.#setLoading(false);
                this.classList.remove('loading');
                this.#dispatchConstructorUpdated(payload, { source, filters, sortBy, sortOrder });
                return Promise.resolve();
            }

            return this.#fetchConstructorResults({
                page,
                resultsPerPage: pageSize,
                filters,
                sortBy,
                sortOrder,
                signal: this.#abortController.signal,
            })
                .then((payload) => {
                    console.log('payload', payload);
                    if (generation !== this.#browseGeneration) {
                        return;
                    }

                    const redirectUrl = payload.response?.redirect?.data?.url;

                    if (this.#isSearch() && redirectUrl) {
                        window.location.assign(redirectUrl);
                        return;
                    }

                    this.#renderBrowsePayload(payload, { append: false, reset, page });
                    this.elProducts.style.opacity = '1';

                    if (updateHistory) {
                        history.pushState(
                            { collectionGridState: true },
                            '',
                            buildCollectionHistoryUrl({
                                page,
                                filters,
                                sortBy,
                                sortOrder,
                            })
                        );
                    }

                    const count = this.#countProductCards(this.elProducts);
                    this.#setLoading(false);
                    this.#announce(this.labelLoaded.replace('%count%', String(count)));
                    this.classList.remove('loading');
                    this.#dispatchConstructorUpdated(payload, { source, filters, sortBy, sortOrder });
                })
                .catch((error) => {
                    if (error?.name === 'AbortError' || generation !== this.#browseGeneration) {
                        return;
                    }

                    this.elProducts.style.opacity = '1';
                    this.#restoreConstructorResultCount();
                    this.#setLoading(false);
                    this.classList.remove('loading');
                    this.#announce(this.labelError);
                    console.error(
                        this.#isSearch() ? 'collection-grid search failed' : 'collection-grid browse failed',
                        error
                    );
                });
        }

        #renderBrowsePayload(payload, { append = false, reset = false, page = 1 } = {}) {
            const response = payload.response || {};
            const results = response.results || [];
            const total = Number(response.total_num_results || 0);
            const pageSize = Number(this.getAttribute('page-size')) || 12;
            const pageTotal = total > 0 ? Math.ceil(total / pageSize) : 0;
            const filtersActive = hasActiveBrowseFilters(this.#browseState?.filters);
            const empty = this.elProducts.querySelector('[data-empty-state]');

            if (!append) {
                this.#plpInserter = filtersActive ? null : createPlpInserter(this.#plpModules);

                if (this.#plpInserter && page > 1) {
                    this.#plpInserter.skipProducts((page - 1) * pageSize, {
                        resetRow: !this.infiniteScroll,
                    });
                }

                this.elProducts.replaceChildren();

                if (empty) {
                    this.elProducts.appendChild(empty);
                }
            }

            const fragment = document.createDocumentFragment();
            const baseIndex = append ? this.#countProductCards(this.elProducts) : 0;
            const cardOptions = {
                collectionId: this.dataset.collectionId,
                showPromotions: this.dataset.showPromotions !== 'false',
                waitlistEnabled: this.dataset.waitlistEnabled === 'true',
                countryCode: this.dataset.countryCode || 'US',
                currencyCode: this.dataset.currencyCode || 'USD',
            };

            results.forEach((result, index) => {
                const globalIndex = baseIndex + index;

                if (this.#plpInserter) {
                    for (const node of this.#plpInserter.beforeProduct()) {
                        fragment.appendChild(node);
                    }
                }

                fragment.appendChild(
                    renderProductCard(this.#cardTemplates, result, {
                        ...cardOptions,
                        eager: globalIndex < 4,
                        lcp: globalIndex === 0 && !append,
                        index: globalIndex,
                    })
                );

                if (this.#plpInserter) {
                    for (const node of this.#plpInserter.afterProduct()) {
                        fragment.appendChild(node);
                    }
                }
            });

            this.elProducts.appendChild(fragment);

            const elClear = empty?.querySelector('[data-empty-clear]');
            elClear?.toggleAttribute('hidden', !filtersActive || results.length > 0);

            this.currentPage = page;
            this.pageTotal = pageTotal;
            this.setAttribute('page-current', String(page));
            this.setAttribute('page-total', String(pageTotal));
            this.setAttribute('data-cnstrc-result-page', String(page));
            this.setAttribute('data-cnstrc-num-results', String(total));
            this.#cnstrcNumResults = null;

            if (payload.result_id) {
                this.setAttribute('data-cnstrc-result-id', payload.result_id);
            }

            if (this.#isSearch()) {
                this.setAttribute('data-cnstrc-search-term', this.#searchQuery());
                this.#updateSearchHeading(total);
            }

            if (this.elCount) {
                this.elCount.textContent = String(total);
            }

            this.#renderBrowsePagination();
            this.#updatePaginationUI();
            this.#updateFooterVisibility();
            this.#promoteViewportImages();
            window.initLazyLoad?.();

            if (reset && this.infiniteScroll) {
                this.#resetInfiniteScrollState();
            } else if (this.infiniteScroll && this.currentPage < this.pageTotal) {
                this.#prefetchBrowseNextPage();
            } else if (this.infiniteScroll && this.currentPage >= this.pageTotal) {
                this.#setComplete();
            }

            return results.length;
        }

        #renderBrowsePagination() {
            if (!this.elPagination || this.infiniteScroll) {
                return;
            }

            this.elPagination.replaceChildren();

            if (this.pageTotal <= 1) {
                return;
            }

            const addPageLink = (page, { current = false, prev = false, next = false } = {}) => {
                const link = document.createElement('a');
                link.href = buildCollectionHistoryUrl({
                    ...this.#browseState,
                    page,
                });
                link.dataset.page = String(page);

                if (prev) {
                    link.className = 'collection-grid--page-prev btn--arrow prev';
                    link.dataset.pagePrev = '';
                    const hidden = document.createElement('span');
                    hidden.className = 'visually-hidden';
                    hidden.textContent = 'Previous';
                    link.appendChild(hidden);
                    this.elPagination.appendChild(link);
                    return;
                }

                if (next) {
                    link.className = 'collection-grid--page-next btn--arrow next';
                    link.dataset.pageNext = '';
                    const hidden = document.createElement('span');
                    hidden.className = 'visually-hidden';
                    hidden.textContent = 'Next';
                    link.appendChild(hidden);
                    this.elPagination.appendChild(link);
                    return;
                }

                const item = document.createElement('li');
                link.className = 'collection-grid--page btn--secondary small';
                link.textContent = String(page);

                if (current) {
                    link.setAttribute('aria-current', 'page');
                }

                item.appendChild(link);
                return item;
            };

            if (this.currentPage > 1) {
                addPageLink(this.currentPage - 1, { prev: true });
            }

            const list = document.createElement('ol');
            list.className = 'collection-grid--page-list';
            let previousPage = 0;

            for (const page of visiblePaginationPages(this.currentPage, this.pageTotal)) {
                const gap = page - previousPage;

                if (previousPage > 0 && gap === 2) {
                    list.appendChild(addPageLink(previousPage + 1));
                } else if (previousPage > 0 && gap > 2) {
                    const ellipsis = document.createElement('li');
                    ellipsis.className = 'collection-grid--page-ellipsis';
                    ellipsis.setAttribute('aria-hidden', 'true');
                    ellipsis.textContent = '…';
                    list.appendChild(ellipsis);
                }

                list.appendChild(addPageLink(page, { current: page === this.currentPage }));
                previousPage = page;
            }

            this.elPagination.appendChild(list);

            if (this.currentPage < this.pageTotal) {
                addPageLink(this.currentPage + 1, { next: true });
            }

            this.elPagePrev = this.querySelector('[data-page-prev]');
            this.elPageNext = this.querySelector('[data-page-next]');
            this.elPageButtons = this.querySelectorAll('[data-page]');
        }

        #prefetchBrowseNextPage() {
            const nextPage = this.currentPage + 1;

            if (this.#prefetching || this.#loading || this.currentPage >= this.pageTotal) {
                return;
            }

            if (this.#pendingBrowse?.page === nextPage) {
                return;
            }

            this.#prefetching = true;
            this.#prefetchAbortController?.abort();
            this.#prefetchAbortController = new AbortController();

            const pageSize = Number(this.getAttribute('page-size')) || 12;
            const { filters, sortBy, sortOrder } = this.#browseState || {};

            this.#fetchConstructorResults({
                page: nextPage,
                resultsPerPage: pageSize,
                filters,
                sortBy,
                sortOrder,
                signal: this.#prefetchAbortController.signal,
            })
                .then((payload) => {
                    this.#pendingBrowse = {
                        page: nextPage,
                        payload,
                    };
                    const first = payload.response?.results?.[0]?.data;
                    if (first?.image_url) {
                        preloadImage(first.image_url, '', '');
                    }
                })
                .catch((error) => {
                    if (error?.name === 'AbortError') {
                        return;
                    }

                    console.error('collection-grid browse prefetch failed', error);
                })
                .finally(() => {
                    this.#prefetching = false;
                });
        }

        #loadBrowseNextPage() {
            if (this.#loading || this.currentPage >= this.pageTotal) {
                return;
            }

            const nextPage = this.currentPage + 1;
            this.#setLoading(true);
            this.#announce(this.labelLoading);

            const apply = (payload) => {
                const count = this.#renderBrowsePayload(payload, { append: true, page: nextPage });
                this.#browseState = { ...this.#browseState, page: nextPage };
                this.#clearPrefetch();
                this.#setLoading(false);
                this.#announce(this.labelLoaded.replace('%count%', String(count)));

                if (this.currentPage >= this.pageTotal) {
                    this.#setComplete();
                }
            };

            if (this.#pendingBrowse?.page === nextPage) {
                apply(this.#pendingBrowse.payload);
                return;
            }

            const pageSize = Number(this.getAttribute('page-size')) || 12;
            const generation = ++this.#browseGeneration;
            this.#abortController?.abort();
            this.#abortController = new AbortController();

            this.#fetchConstructorResults({
                page: nextPage,
                resultsPerPage: pageSize,
                filters: this.#browseState?.filters,
                sortBy: this.#browseState?.sortBy,
                sortOrder: this.#browseState?.sortOrder,
                signal: this.#abortController.signal,
            })
                .then((payload) => {
                    if (generation !== this.#browseGeneration) {
                        return;
                    }

                    apply(payload);
                })
                .catch((error) => {
                    if (error?.name === 'AbortError') {
                        return;
                    }

                    this.#setLoading(false);
                    this.#announce(this.labelError);
                    console.error('collection-grid browse failed', error);
                });
        }
    }

    customElements.define('collection-grid', CollectionGrid);
}
